import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useTask,
  useUpdateTask,
  useChangeTaskStatus,
  useAssignTask,
  useUnassignTask,
  useAddChecklistItem,
  useToggleChecklistItem,
  useRemoveChecklistItem,
  useDirectory,
} from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  type Tab,
  type EditForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  PRIORITY_LABELS,
  PRIORITY_VARIANTS,
  CATEGORY_LABELS,
  editFormSchema,
  SkeletonDetail,
  primaryBtn,
  secondaryBtn,
  linkBtn,
  modalOverlay,
} from './task-detail.support';

import { TaskChecklistTab } from './TaskChecklistTab';
import { TaskAssignTab } from './TaskAssignTab';
import { TaskDadosTab } from './task-detail.dados-tab';

export function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: task, isLoading, isError } = useTask(id ?? '');
  const updateTask = useUpdateTask();
  const changeStatus = useChangeTaskStatus();
  const assignTask = useAssignTask();
  const unassignTask = useUnassignTask();
  const addChecklistItem = useAddChecklistItem();
  const toggleChecklistItem = useToggleChecklistItem();
  const removeChecklistItem = useRemoveChecklistItem();

  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dados');
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [personSearch, setPersonSearch] = useState('');
  const [newChecklistLabel, setNewChecklistLabel] = useState('');

  const t = asRecord(task);
  const st = str(t.status);
  const pr = str(t.priority);
  const cat = str(t.category);

  const assignments = (t.assignments as Record<string, unknown>[] | undefined) ?? [];
  const checklistItems = (t.checklistItems as Record<string, unknown>[] | undefined) ?? [];

  const { data: peopleData } = useDirectory(
    personSearch ? { search: personSearch, limit: 50 } : { limit: 50 },
  );
  const people = asList(peopleData);

  const form = useForm<EditForm>({
    resolver: zodResolver(editFormSchema),
    values: {
      title: str(t.title),
      priority: pr,
      category: cat,
      description: str(t.description),
      dueDate: typeof t.dueDate === 'string' ? t.dueDate.substring(0, 10) : '',
      eventId: str(t.eventId),
      obligationId: str(t.obligationId),
    },
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>
            Não foi possível carregar esta tarefa.
          </p>
          <button
            type="button"
            onClick={() => {
              void navigate('/tarefas');
            }}
            style={linkBtn}
          >
            ← Voltar para tarefas
          </button>
        </Section>
      </div>
    );
  }

  function handleSave(data: EditForm) {
    if (!id) return;
    updateTask.mutate({
      id,
      data: {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined,
      },
    });
    setEditing(false);
  }

  function handleStatusChange() {
    if (!newStatus || !id) return;
    changeStatus.mutate({ id, status: newStatus });
    setStatusChangeOpen(false);
    setNewStatus('');
  }

  function handleAssign(personId: string) {
    if (!id) return;
    assignTask.mutate({ id, personId });
    setAssignOpen(false);
  }

  function handleUnassign(assignmentId: string) {
    if (!id) return;
    unassignTask.mutate({ id, assignmentId });
  }

  function handleAddChecklistItem() {
    if (!id || !newChecklistLabel.trim()) return;
    addChecklistItem.mutate({ id, label: newChecklistLabel.trim() });
    setNewChecklistLabel('');
  }

  function handleToggleChecklistItem(itemId: string, completed: boolean) {
    if (!id) return;
    toggleChecklistItem.mutate({ id, itemId, completed: !completed });
  }

  function handleRemoveChecklistItem(itemId: string) {
    if (!id) return;
    removeChecklistItem.mutate({ id, itemId });
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/tarefas');
        }}
        style={linkBtn}
      >
        ← Voltar para tarefas
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{str(t.title)}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            <Badge label={CATEGORY_LABELS[cat] ?? cat} variant="info" />
            <Badge label={PRIORITY_LABELS[pr] ?? pr} variant={PRIORITY_VARIANTS[pr] ?? 'default'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
              }}
              style={secondaryBtn}
            >
              Editar
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setStatusChangeOpen(true);
              }}
              style={{
                ...secondaryBtn,
                background: 'var(--warning-soft)',
                borderColor: 'var(--warning)',
                color: 'var(--warning)',
              }}
            >
              Alterar status
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: '1rem',
          borderBottom: '2px solid var(--line)',
        }}
      >
        {(
          [
            ['dados', 'Dados'],
            ['checklist', `Lista de verificação (${String(checklistItems.length)})`],
            ['atribuidos', `Atribuídos (${String(assignments.length)})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveTab(key);
            }}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: activeTab === key ? 600 : 400,
              color: activeTab === key ? 'var(--green)' : 'var(--muted)',
              border: 'none',
              borderBottom: activeTab === key ? '2px solid var(--green)' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Modal: Alterar status */}
        {statusChangeOpen && (
          <div
            style={modalOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setStatusChangeOpen(false);
              }
            }}
          >
            <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', minWidth: 360 }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Alterar status</h3>
              <select
                value={newStatus}
                onChange={(e) => {
                  setNewStatus(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  fontSize: '0.875rem',
                  marginBottom: '0.75rem',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Selecione...</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v} disabled={v === st}>
                    {l}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setStatusChangeOpen(false);
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleStatusChange}
                  disabled={!newStatus || changeStatus.isPending}
                  style={primaryBtn}
                >
                  {changeStatus.isPending ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Dados */}
        {activeTab === 'dados' && (
          <TaskDadosTab
            editing={editing}
            form={form}
            t={t}
            cat={cat}
            pr={pr}
            st={st}
            updatePending={updateTask.isPending}
            onCancelEdit={() => {
              setEditing(false);
            }}
            onSave={handleSave}
          />
        )}

        {activeTab === 'checklist' && (
          <TaskChecklistTab
            items={checklistItems}
            newLabel={newChecklistLabel}
            onNewLabelChange={setNewChecklistLabel}
            onAdd={handleAddChecklistItem}
            adding={addChecklistItem.isPending}
            onToggle={handleToggleChecklistItem}
            toggling={toggleChecklistItem.isPending}
            onRemove={handleRemoveChecklistItem}
            removing={removeChecklistItem.isPending}
          />
        )}
        {activeTab === 'atribuidos' && (
          <TaskAssignTab
            assignments={assignments}
            people={people}
            assignOpen={assignOpen}
            personSearch={personSearch}
            onOpenAssign={() => {
              setAssignOpen(true);
            }}
            onClose={() => {
              setAssignOpen(false);
              setPersonSearch('');
            }}
            onPersonSearchChange={setPersonSearch}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            unassigning={unassignTask.isPending}
          />
        )}
      </div>
    </div>
  );
}
